import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getAnalytics,
  getImageHistogram,
  listAnalyticsImages,
  type AnalyticsData,
  type AnalyticsImageTransformStat,
  type ImageHistogramData,
} from '../services/dashboardApi';
import { MediaMagicProvider, MagicImage } from '@media-magic/sdk';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface AnalyticsProps {
  userId: string;
}

const IMAGE_PAGE_SIZE = 10;
const IMAGE_FETCH_LIMIT = 100;
type ImageSortKey = 'originalFilename' | 'transformCount' | 'minRequestedArea' | 'maxRequestedArea';
type SortDirection = 'asc' | 'desc';
type AnalyticsRangePreset = '1d' | '3d' | '7d' | '30d' | 'custom';
const RANGE_PRESETS: AnalyticsRangePreset[] = ['1d', '3d', '7d', '30d', 'custom'];
const RANGE_PRESET_DAYS: Record<Exclude<AnalyticsRangePreset, 'custom'>, number> = {
  '1d': 1,
  '3d': 3,
  '7d': 7,
  '30d': 30,
};

function formatInputDate(value: Date): string {
  const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 10);
}

function getPresetRange(preset: Exclude<AnalyticsRangePreset, 'custom'>): { fromDate: string; toDate: string } {
  const now = new Date();
  const toDate = formatInputDate(now);
  const days = RANGE_PRESET_DAYS[preset];
  const fromDate = formatInputDate(new Date(now.getTime() - (days - 1) * 24 * 60 * 60 * 1000));
  return { fromDate, toDate };
}

function inclusiveDaysBetween(fromDate: string, toDate: string): number {
  const from = Date.parse(`${fromDate}T00:00:00Z`);
  const to = Date.parse(`${toDate}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return 1;
  return Math.max(1, Math.floor((to - from) / (24 * 60 * 60 * 1000)) + 1);
}

function InlineSpinner({ size = 14, color = 'var(--surface-white)' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" aria-hidden="true">
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="4" opacity="0.25" />
      <path d="M45 25a20 20 0 0 1-20 20" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 25 25"
          to="360 25 25"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}

export function Analytics({ userId }: AnalyticsProps) {
  const initialRange = getPresetRange('7d');
  const [rangePreset, setRangePreset] = useState<AnalyticsRangePreset>('7d');
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [transformRows, setTransformRows] = useState<AnalyticsImageTransformStat[]>([]);
  const [histograms, setHistograms] = useState<Record<string, ImageHistogramData>>({});
  const [currentImagePage, setCurrentImagePage] = useState(0);
  const [tableSort, setTableSort] = useState<{ key: ImageSortKey; direction: SortDirection }>({
    key: 'transformCount',
    direction: 'desc',
  });
  const [fromDate, setFromDate] = useState<string>(initialRange.fromDate);
  const [toDate, setToDate] = useState<string>(initialRange.toDate);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompact, setIsCompact] = useState(false);
  const [isPhone, setIsPhone] = useState(false);
  const hasLoadedAnalyticsRef = useRef(false);

  const applyPreset = useCallback((preset: AnalyticsRangePreset) => {
    setRangePreset(preset);
    if (preset === 'custom') {
      return;
    }
    const nextRange = getPresetRange(preset);
    setFromDate(nextRange.fromDate);
    setToDate(nextRange.toDate);
  }, []);

  const fetchAnalytics = useCallback(async () => {
    const isInitialLoad = !hasLoadedAnalyticsRef.current;
    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      const data = await getAnalytics(userId, fromDate, toDate);
      setAnalytics(data);
      hasLoadedAnalyticsRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, fromDate, toDate]);

  const fetchTransformRows = useCallback(async () => {
    try {
      setImagesLoading(true);
      const response = await listAnalyticsImages(
        userId,
        IMAGE_FETCH_LIMIT,
        undefined,
        tableSort.key,
        tableSort.direction
      );
      setTransformRows(response.items || []);
      setHistograms({});
      setCurrentImagePage(0);
    } catch (err) {
      if (!error) {
        setError(err instanceof Error ? err.message : 'Failed to load per-image analytics');
      }
    } finally {
      setImagesLoading(false);
    }
  }, [userId, error, tableSort.key, tableSort.direction]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchTransformRows();
  }, [fetchTransformRows]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const compactQuery = window.matchMedia('(max-width: 920px)');
    const phoneQuery = window.matchMedia('(max-width: 640px)');
    const apply = () => {
      setIsCompact(compactQuery.matches);
      setIsPhone(phoneQuery.matches);
    };
    apply();
    compactQuery.addEventListener('change', apply);
    phoneQuery.addEventListener('change', apply);
    return () => {
      compactQuery.removeEventListener('change', apply);
      phoneQuery.removeEventListener('change', apply);
    };
  }, []);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const formatSecondsFromMs = (ms: number): string => {
    const seconds = Math.max(0, ms || 0) / 1000;
    return `${seconds.toFixed(seconds >= 10 ? 1 : 2)}s`;
  };
  const getDurationTone = (ms: number): React.CSSProperties => {
    const value = Math.max(0, ms || 0);
    if (value >= 2500) return { color: 'var(--danger-700)' };
    if (value >= 1000) return { color: 'var(--warning-600)' };
    if (value <= 350) return { color: 'var(--primary-700)' };
    return { color: 'var(--success-700)' };
  };
  const getErrorTone = (count: number): React.CSSProperties =>
    (count || 0) > 0 ? { color: 'var(--danger-700)' } : { color: 'var(--primary-700)' };
  const getCacheTone = (ratio: number): React.CSSProperties => {
    const r = Math.max(0, Math.min(1, ratio || 0));
    if (r < 0.5) return { color: 'var(--danger-700)' };
    if (r < 0.75) return { color: 'var(--warning-600)' };
    if (r >= 0.9) return { color: 'var(--primary-700)' };
    return { color: 'var(--success-700)' };
  };

  const formatTransformSize = (row: AnalyticsImageTransformStat, mode: 'min' | 'max'): string => {
    const explicit = (mode === 'min' ? row.minRequestedSize : row.maxRequestedSize) || '';
    if (/^\d+x\d+$/i.test(explicit)) {
      return explicit.toLowerCase();
    }

    const area = Math.max(0, mode === 'min' ? row.minRequestedArea || 0 : row.maxRequestedArea || 0);
    if (!area) return '—';

    const originalWidth = Math.max(0, row.originalWidth || 0);
    const originalHeight = Math.max(0, row.originalHeight || 0);
    if (originalWidth > 0 && originalHeight > 0) {
      const ratio = originalWidth / originalHeight;
      const width = Math.max(1, Math.round(Math.sqrt(area * ratio)));
      const height = Math.max(1, Math.round(width / ratio));
      return `${width}x${height}`;
    }

    const side = Math.max(1, Math.round(Math.sqrt(area)));
    return `${side}x${side}`;
  };
  const CDN_URL = import.meta.env.VITE_MM_CDN_URL || '';
  const maxPage = Math.max(1, Math.ceil(transformRows.length / IMAGE_PAGE_SIZE));
  const currentRows = useMemo(
    () => transformRows.slice(currentImagePage * IMAGE_PAGE_SIZE, (currentImagePage + 1) * IMAGE_PAGE_SIZE),
    [transformRows, currentImagePage]
  );
  useEffect(() => {
    let cancelled = false;
    const missingMediaIds = currentRows
      .map((row) => row.mediaId)
      .filter((mediaId) => mediaId && !histograms[mediaId]);

    if (!missingMediaIds.length) {
      return () => {
        cancelled = true;
      };
    }

    const loadHistograms = async () => {
      const results = await Promise.allSettled(
        missingMediaIds.map(async (mediaId) => ({ mediaId, data: await getImageHistogram(userId, mediaId) }))
      );
      if (cancelled) return;

      setHistograms((prev) => {
        const next = { ...prev };
        for (const result of results) {
          if (result.status === 'fulfilled') {
            next[result.value.mediaId] = result.value.data;
          }
        }
        return next;
      });
    };

    void loadHistograms();
    return () => {
      cancelled = true;
    };
  }, [currentRows, histograms, userId]);

  const histogramSeries = (mediaId: string) => {
    const histogram = histograms[mediaId];
    if (!histogram || !Array.isArray(histogram.bins)) {
      return null;
    }
    const bins = histogram.bins
      .filter((bin) => bin && bin.bin !== 'OTHER' && Number(bin.count) > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
    if (!bins.length) return null;
    const data = bins.map((bin) => ({
      label: bin.bin,
      value: bin.count,
    }));
    return {
      data,
      asOf: histogram.asOf,
      total: histogram.totalTransforms || data.reduce((acc, item) => acc + item.value, 0),
    };
  };

  const toggleSort = (key: ImageSortKey) => {
    setTableSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: key === 'originalFilename' ? 'asc' : 'desc' };
    });
  };
  const sortMarker = (key: ImageSortKey): string => {
    if (tableSort.key !== key) return '↕';
    return tableSort.direction === 'asc' ? '↑' : '↓';
  };
  const avgPreprocessTone = getDurationTone(analytics?.summary.avgPreprocessMs || 0);
  const avgTransformTone = getDurationTone(analytics?.summary.avgTransformMs || 0);
  const cacheTone = getCacheTone(analytics?.cacheHitRatio || 0);
  const preprocessErrorTone = getErrorTone(analytics?.summary.preprocessErrors || 0);
  const transformErrorTone = getErrorTone(analytics?.summary.transformErrors || 0);

  if (loading) {
    return (
      <div className="portal-surface" style={styles.container}>
        <div style={styles.loading}>Loading analytics...</div>
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="portal-surface" style={styles.container}>
        <div style={styles.error}>
          <p>{error}</p>
          <button onClick={fetchAnalytics} style={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const summaryFromDate = analytics.summary.fromDate || fromDate;
  const summaryToDate = analytics.summary.toDate || toDate;
  const dayWindow = Math.min(30, inclusiveDaysBetween(summaryFromDate, summaryToDate));
  const dailyUploads = Object.entries(analytics.dailyUploads)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-dayWindow);
  const dailyTransforms = Object.entries(analytics.dailyTransforms || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-dayWindow);

  const uploadsChartData = dailyUploads.map(([date, count]) => ({
    date,
    label: formatDate(date),
    value: count,
  }));
  const transformsChartData = dailyTransforms.map(([date, count]) => ({
    date,
    label: formatDate(date),
    value: count,
  }));
  const cacheRatioChartData = (analytics.cacheHitRatioSeries || [])
    .slice()
    .sort((a, b) => a.ts.localeCompare(b.ts))
    .map((point) => {
      const parsed = new Date(point.ts);
      const label = Number.isNaN(parsed.getTime())
        ? point.ts
        : parsed.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
      const shortLabel = Number.isNaN(parsed.getTime())
        ? point.ts
        : parsed.toLocaleTimeString('en-US', { hour: 'numeric' });
      return {
        ts: point.ts,
        label,
        shortLabel,
        ratioPct: Math.max(0, Math.min(100, (point.ratio || 0) * 100)),
        hitRequests: Math.max(0, point.hitRequests || 0),
        totalRequests: Math.max(0, point.totalRequests || 0),
        transformCount: Math.max(0, point.transformCount || point.totalRequests || 0),
      };
    });
  const uploadTickInterval = Math.max(0, Math.ceil(uploadsChartData.length / 10) - 1);
  const transformTickInterval = Math.max(0, Math.ceil(transformsChartData.length / 10) - 1);
  const cacheTickInterval = Math.max(0, Math.ceil(cacheRatioChartData.length / 8) - 1);

  return (
    <MediaMagicProvider config={{ clientId: userId, cdnUrl: CDN_URL }}>
    <div
      className="portal-surface"
      style={{
        ...styles.container,
        ...(isCompact ? styles.containerCompact : {}),
      }}
    >
      <div style={styles.header}>
        <h1 style={styles.title}>Insights</h1>
      </div>

      <div style={styles.sectionShell}>
        <div style={{ ...styles.kpiRow, ...(isPhone ? styles.kpiRowPhone : {}) }}>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Total Images</div>
            <div style={styles.kpiValue}>{analytics.summary.totalImages}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Total Storage</div>
            <div style={styles.kpiValue}>{formatBytes(analytics.summary.totalSizeBytes)}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Faces Detected</div>
            <div style={styles.kpiValue}>{analytics.summary.facesDetected ?? '—'}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>Objects Detected</div>
            <div style={styles.kpiValue}>{analytics.summary.objectsDetected ?? '—'}</div>
          </div>
          <div style={styles.kpiCard}>
            <div style={styles.kpiLabel}>CDN Cache Hit Ratio (24h rolling)</div>
            <div style={{ ...styles.kpiValue, ...cacheTone }}>{Math.round(analytics.cacheHitRatio * 100)}%</div>
          </div>
        </div>
      </div>

      <div style={styles.sectionShell}>
        <div style={styles.sectionToolbar}>
          <div style={{ ...styles.controlsRow, ...(isCompact ? styles.controlsRowCompact : {}) }}>
            <div style={{ ...styles.presetGroup, ...(isPhone ? styles.presetGroupPhone : {}) }}>
              {RANGE_PRESETS.map((preset) => (
                <button
                  key={preset}
                  onClick={() => applyPreset(preset)}
                  style={{
                    ...styles.presetButton,
                    ...(rangePreset === preset ? styles.presetButtonActive : {}),
                  }}
                >
                  {preset === 'custom' ? 'Custom' : preset}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                fetchAnalytics();
                fetchTransformRows();
              }}
              style={{ ...styles.refreshButton, ...(isPhone ? styles.refreshButtonPhone : {}) }}
              disabled={loading || refreshing}
              aria-label={refreshing ? 'Refreshing analytics' : 'Refresh analytics'}
            >
              {refreshing ? <InlineSpinner /> : 'Refresh'}
            </button>
            {rangePreset === 'custom' && (
              <div style={{ ...styles.customDateGroup, ...(isPhone ? styles.customDateGroupPhone : {}) }}>
                <input
                  type="date"
                  aria-label="From date"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => {
                    setRangePreset('custom');
                    setFromDate(e.target.value);
                  }}
                  style={styles.dateInput}
                />
                <span style={styles.dateRangeDivider}>to</span>
                <input
                  type="date"
                  aria-label="To date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => {
                    setRangePreset('custom');
                    setToDate(e.target.value);
                  }}
                  style={styles.dateInput}
                />
              </div>
            )}
          </div>
        </div>
        <div style={{ ...styles.summaryGrid, ...(isPhone ? styles.summaryGridPhone : {}) }}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryValue}>{analytics.summary.totalTransforms || 0}</div>
            <div style={styles.summaryLabel}>Total Transforms</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, ...avgPreprocessTone }}>{formatSecondsFromMs(analytics.summary.avgPreprocessMs || 0)}</div>
            <div style={styles.summaryLabel}>Avg Preprocess Time</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, ...avgTransformTone }}>{formatSecondsFromMs(analytics.summary.avgTransformMs || 0)}</div>
            <div style={styles.summaryLabel}>Avg Transform Time</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, ...preprocessErrorTone }}>{analytics.summary.preprocessErrors || 0}</div>
            <div style={styles.summaryLabel}>Preprocess Errors</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={{ ...styles.summaryValue, ...transformErrorTone }}>{analytics.summary.transformErrors || 0}</div>
            <div style={styles.summaryLabel}>Transform Errors</div>
          </div>
        </div>
        <div style={{ ...styles.timeWindowChartsGrid, ...(isCompact ? styles.timeWindowChartsGridCompact : {}) }}>
          <div style={styles.timeWindowChartCard}>
            <h3 style={styles.panelTitle}>Daily Uploads</h3>
            <div style={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={isPhone ? 188 : 220}>
                <BarChart data={uploadsChartData} margin={{ top: 6, right: 8, left: -20, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line-soft)" />
                  <XAxis
                    dataKey="label"
                    interval={uploadTickInterval}
                    tick={{ fontSize: 11, fill: 'var(--ink-500)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'var(--line-soft)' }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--ink-500)' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(59,130,246,0.08)' }}
                    labelFormatter={(_, payload) => (payload?.[0]?.payload?.date ? `Date: ${payload[0].payload.date}` : '')}
                    formatter={(value) => [`${value ?? 0}`, 'Uploads']}
                  />
                  <Bar dataKey="value" fill="var(--primary-600)" radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={styles.timeWindowChartCard}>
            <h3 style={styles.panelTitle}>Daily Transforms</h3>
            <div style={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={isPhone ? 188 : 220}>
                <BarChart data={transformsChartData} margin={{ top: 6, right: 8, left: -20, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line-soft)" />
                  <XAxis
                    dataKey="label"
                    interval={transformTickInterval}
                    tick={{ fontSize: 11, fill: 'var(--ink-500)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'var(--line-soft)' }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--ink-500)' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(46,125,50,0.08)' }}
                    labelFormatter={(_, payload) => (payload?.[0]?.payload?.date ? `Date: ${payload[0].payload.date}` : '')}
                    formatter={(value) => [`${value ?? 0}`, 'Transforms']}
                  />
                  <Bar dataKey="value" fill="var(--success-700)" radius={[4, 4, 0, 0]} maxBarSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div style={styles.cacheTrendCard}>
          <h3 style={styles.panelTitle}>Cache Ratio + Transform Requests (Hourly)</h3>
          <div style={styles.cacheTrendMeta}>Showing available points (up to 7 days)</div>
          {cacheRatioChartData.length > 0 ? (
            <div style={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={isPhone ? 188 : 220}>
                <ComposedChart data={cacheRatioChartData} margin={{ top: 8, right: 12, left: -8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--line-soft)" />
                  <XAxis
                    dataKey={isPhone ? 'shortLabel' : 'label'}
                    interval={cacheTickInterval}
                    tick={{ fontSize: 11, fill: 'var(--ink-500)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'var(--line-soft)' }}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                    tick={{ fontSize: 11, fill: 'var(--ink-500)' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: 'var(--ink-500)' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ stroke: 'var(--primary-300)', strokeDasharray: '4 4' }}
                    labelFormatter={(_, payload) => {
                      const row = payload?.[0]?.payload;
                      if (!row) return '';
                      return `${row.ts} • hits ${row.hitRequests}/${row.totalRequests}`;
                    }}
                    formatter={(value, name) =>
                      name === 'Cache hit ratio'
                        ? [`${Number(value).toFixed(2)}%`, name]
                        : [`${value ?? 0}`, name]
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    yAxisId="right"
                    name="Transform requests"
                    dataKey="transformCount"
                    fill="var(--primary-500)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={18}
                  />
                  <Line
                    yAxisId="left"
                    name="Cache hit ratio"
                    type="monotone"
                    dataKey="ratioPct"
                    stroke="var(--success-700)"
                    strokeWidth={2}
                    dot={{ r: 2.2, fill: 'var(--success-700)' }}
                    activeDot={{ r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={styles.cacheTrendEmpty}>
              Not enough cache/transform history yet.
            </div>
          )}
        </div>
      </div>

      <div style={styles.panel}>
        <h3 style={styles.panelTitle}>Transforms at Glance (Top 100)</h3>
        {currentRows.length === 0 && !imagesLoading && <div style={styles.emptyState}>No transformed images yet</div>}
        {currentRows.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={{ ...styles.table, ...(isPhone ? styles.tablePhone : {}) }}>
              <thead>
                <tr>
                  <th style={styles.thLeft}>
                    <button style={styles.sortButton} onClick={() => toggleSort('originalFilename')}>
                      Image {sortMarker('originalFilename')}
                    </button>
                  </th>
                  <th style={styles.thRight}>
                    <button style={styles.sortButton} onClick={() => toggleSort('transformCount')}>
                      Transforms {sortMarker('transformCount')}
                    </button>
                  </th>
                  <th style={styles.thRight}>
                    <button style={styles.sortButton} onClick={() => toggleSort('minRequestedArea')}>
                      Smallest Transform {sortMarker('minRequestedArea')}
                    </button>
                  </th>
                  <th style={styles.thRight}>
                    <button style={styles.sortButton} onClick={() => toggleSort('maxRequestedArea')}>
                      Largest Transform {sortMarker('maxRequestedArea')}
                    </button>
                  </th>
                  {!isPhone && <th style={{ ...styles.thLeft, ...styles.thHistogram }}>Size Histogram (30d)</th>}
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => {
                  const histogram = histograms[row.mediaId];
                  const series = histogramSeries(row.mediaId);
                  const asOfLabel = series?.asOf ? new Date(series.asOf).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                  return (
                  <tr key={row.mediaId}>
                    <td style={styles.tdLeft}>
                      <div style={styles.imageCell}>
                        <MagicImage
                          src={row.mediaId}
                          alt={row.originalFilename || row.mediaId}
                          width={36}
                          height={36}
                          crop
                          autofocus="smart"
                          layout="fixed"
                          autoSize={true}
                          style={styles.tableThumb}
                          loading="lazy"
                        />
                        <div>
                          <div style={styles.tableName}>{row.originalFilename || row.mediaId.slice(0, 8)}</div>
                          <div style={styles.tableMeta}>{row.mediaId}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.tdRight}>{row.transformCount}</td>
                    <td style={styles.tdRight}>{formatTransformSize(row, 'min')}</td>
                    <td style={styles.tdRight}>{formatTransformSize(row, 'max')}</td>
                    {!isPhone && (
                    <td style={{ ...styles.tdLeft, ...styles.tdHistogram }}>
                      {series ? (
                        <div style={styles.histogramWrap}>
                          <div style={styles.histogramChartBox}>
                            <ResponsiveContainer width="100%" height={86}>
                              <BarChart
                                data={series.data}
                                layout="vertical"
                                margin={{ top: 4, right: 4, bottom: 4, left: 0 }}
                              >
                                <XAxis type="number" hide domain={[0, 'dataMax']} />
                                <YAxis
                                  type="category"
                                  dataKey="label"
                                  width={58}
                                  tick={{ fontSize: 9, fill: 'var(--ink-600)' }}
                                  tickLine={false}
                                  axisLine={false}
                                />
                                <Tooltip
                                  cursor={{ fill: 'rgba(12,108,142,0.08)' }}
                                  labelFormatter={(label) => `Size: ${label}`}
                                  formatter={(value) => [`${value}`, 'Requests']}
                                />
                                <Bar dataKey="value" fill="var(--primary-500)" radius={[4, 4, 4, 4]} barSize={10} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div style={styles.histAxisLabels}>
                            total {series.total} · as of {asOfLabel}
                          </div>
                        </div>
                      ) : (
                        <div style={histogram ? styles.histEmpty : styles.histLoading}>
                          {histogram ? 'No histogram data yet' : 'Loading...'}
                        </div>
                      )}
                    </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ ...styles.paginationRow, ...(isPhone ? styles.paginationRowPhone : {}) }}>
          <button
            style={styles.pageButton}
            onClick={() => setCurrentImagePage((prev) => Math.max(0, prev - 1))}
            disabled={imagesLoading || currentImagePage === 0}
          >
            Previous
          </button>
          <div style={styles.pageLabel}>Page {currentImagePage + 1} / {maxPage}</div>
          <button
            style={styles.pageButton}
            onClick={() => setCurrentImagePage((prev) => Math.min(maxPage - 1, prev + 1))}
            disabled={imagesLoading || currentImagePage >= maxPage - 1}
          >
            Next
          </button>
        </div>
        {imagesLoading && (
          <div style={{ marginTop: '8px', color: 'var(--ink-500)', fontSize: '12px' }}>
            Loading page...
          </div>
        )}
      </div>

    </div>
    </MediaMagicProvider>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  containerCompact: {
    padding: '14px',
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '14px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: 'var(--ink-900)',
    margin: 0,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  controlsRowCompact: {
    justifyContent: 'flex-start',
  },
  customDateGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 8px',
    borderRadius: '10px',
    border: '1px solid var(--line-soft)',
    backgroundColor: 'var(--surface-2)',
  },
  customDateGroupPhone: {
    width: '100%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  dateRangeDivider: {
    fontSize: '12px',
    color: 'var(--ink-500)',
    fontWeight: 500,
  },
  presetGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flexWrap: 'wrap',
    padding: '3px',
    borderRadius: '12px',
    backgroundColor: 'var(--surface-1)',
    border: '1px solid var(--line-soft)',
  },
  presetGroupPhone: {
    width: 'auto',
    flex: '1 1 auto',
    minWidth: 0,
  },
  presetButton: {
    padding: '6px 10px',
    borderRadius: '9px',
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    color: 'var(--ink-600)',
    fontSize: '12px',
    cursor: 'pointer',
    lineHeight: 1.2,
    fontWeight: 600,
  },
  presetButtonActive: {
    borderColor: 'var(--primary-200)',
    color: 'var(--primary-700)',
    backgroundColor: 'var(--surface-white)',
    fontWeight: '600',
  },
  controlLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: '12px',
    color: 'var(--ink-600)',
  },
  dateInput: {
    border: '1px solid var(--line-strong)',
    borderRadius: '6px',
    padding: '6px 7px',
    fontSize: '12px',
    backgroundColor: 'var(--surface-white)',
    color: 'var(--ink-900)',
  },
  refreshButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--primary-600)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    minWidth: '96px',
    minHeight: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  refreshButtonPhone: {
    marginLeft: 'auto',
  },
  sectionHeader: {
    marginBottom: '10px',
  },
  sectionShell: {
    backgroundColor: 'var(--surface-white)',
    border: '1px solid var(--line-soft)',
    borderRadius: '14px',
    padding: '14px',
    marginBottom: '14px',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
  },
  sectionToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '12px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: '700',
    color: 'var(--ink-900)',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--ink-600)',
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--danger-700)',
  },
  retryButton: {
    marginTop: '16px',
    padding: '8px 16px',
    backgroundColor: 'var(--primary-600)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    marginBottom: '0px',
  },
  summaryGridPhone: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  timeWindowChartsGrid: {
    marginTop: '12px',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
  },
  timeWindowChartsGridCompact: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
  timeWindowChartCard: {
    border: '1px solid var(--line-soft)',
    borderRadius: '10px',
    padding: '12px',
    backgroundColor: 'var(--surface-2)',
    minWidth: 0,
  },
  cacheTrendCard: {
    marginTop: '12px',
    border: '1px solid var(--line-soft)',
    borderRadius: '10px',
    padding: '12px',
    backgroundColor: 'var(--surface-2)',
    minWidth: 0,
  },
  chartWrap: {
    width: '100%',
    height: '220px',
  },
  cacheTrendEmpty: {
    color: 'var(--ink-500)',
    fontSize: '12px',
    padding: '10px 0 4px',
  },
  cacheTrendMeta: {
    color: 'var(--ink-500)',
    fontSize: '12px',
    marginTop: '-8px',
    marginBottom: '8px',
  },
  summaryCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  summaryValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: 'var(--ink-900)',
    lineHeight: 1.1,
  },
  summaryLabel: {
    fontSize: '12px',
    color: 'var(--ink-600)',
    marginTop: '6px',
  },
  kpiRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '12px',
    marginBottom: '0px',
  },
  kpiRowPhone: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  kpiCard: {
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--line-soft)',
    borderRadius: '10px',
    padding: '12px 14px',
  },
  kpiLabel: {
    fontSize: '12px',
    color: 'var(--ink-600)',
  },
  kpiValue: {
    marginTop: '4px',
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--ink-900)',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  panel: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  panelTitle: {
    margin: '0 0 14px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--ink-900)',
  },
  barChart: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    height: '150px',
    gap: '6px',
  },
  barChartScroller: {
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '4px',
  },
  barContainer: {
    width: '24px',
    flex: '0 0 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
  },
  bar: {
    width: '100%',
    backgroundColor: 'var(--primary-600)',
    borderRadius: '4px 4px 0 0',
    minHeight: '4px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginTop: 'auto',
  },
  barValue: {
    fontSize: '12px',
    color: 'white',
    fontWeight: '600',
    transform: 'translateY(-20px)',
  },
  barLabel: {
    fontSize: '11px',
    color: 'var(--ink-600)',
    marginTop: '8px',
  },
  highlightWrap: {
    padding: '12px',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--line-soft)',
    borderRadius: '10px',
  },
  highlightRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
  },
  highlightThumb: {
    width: '44px',
    height: '44px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid var(--line-soft)',
    backgroundColor: 'var(--surface-1)',
    flexShrink: 0,
  },
  highlightContent: {
    minWidth: 0,
  },
  highlightName: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--ink-900)',
    marginBottom: '6px',
  },
  highlightMeta: {
    fontSize: '12px',
    color: 'var(--ink-600)',
    marginBottom: '2px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '660px',
  },
  tablePhone: {
    minWidth: '500px',
  },
  thLeft: {
    textAlign: 'left',
    fontSize: '12px',
    color: 'var(--ink-600)',
    borderBottom: '1px solid var(--line-soft)',
    padding: '8px 6px',
  },
  thRight: {
    textAlign: 'right',
    fontSize: '12px',
    color: 'var(--ink-600)',
    borderBottom: '1px solid var(--line-soft)',
    padding: '8px 6px',
  },
  sortButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: '12px',
    color: 'var(--ink-600)',
    fontWeight: '600',
    width: '100%',
    textAlign: 'inherit',
  },
  tdLeft: {
    textAlign: 'left',
    borderBottom: '1px solid var(--line-soft)',
    padding: '10px 6px',
    verticalAlign: 'top',
  },
  imageCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: 0,
  },
  tableThumb: {
    width: '36px',
    height: '36px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid var(--line-soft)',
    backgroundColor: 'var(--surface-1)',
    flexShrink: 0,
  },
  tdRight: {
    textAlign: 'right',
    borderBottom: '1px solid var(--line-soft)',
    padding: '10px 6px',
    fontSize: '13px',
    color: 'var(--ink-900)',
    whiteSpace: 'nowrap',
  },
  thHistogram: {
    width: '220px',
    paddingLeft: '14px',
  },
  tdHistogram: {
    width: '220px',
    paddingLeft: '14px',
    paddingRight: '2px',
  },
  histogramWrap: {
    width: '214px',
  },
  histogramChartBox: {
    width: '214px',
    backgroundColor: 'var(--surface-2)',
    borderRadius: '6px',
    padding: '6px 4px 4px',
    border: '1px solid var(--line-soft)',
  },
  histAxisLabels: {
    marginTop: '4px',
    display: 'block',
    color: 'var(--ink-500)',
    fontSize: '10px',
    lineHeight: 1.1,
  },
  histLoading: {
    color: 'var(--ink-500)',
    fontSize: '11px',
  },
  histEmpty: {
    color: 'var(--ink-300)',
    fontSize: '11px',
  },
  paginationRow: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  paginationRowPhone: {
    justifyContent: 'center',
  },
  pageButton: {
    padding: '6px 10px',
    border: '1px solid var(--line-strong)',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '12px',
  },
  pageLabel: {
    fontSize: '12px',
    color: 'var(--ink-600)',
    minWidth: '64px',
    textAlign: 'center',
  },
  tableName: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--ink-900)',
  },
  tableMeta: {
    fontSize: '11px',
    color: 'var(--ink-500)',
    marginTop: '2px',
    fontFamily: 'monospace',
  },
  emptyState: {
    color: 'var(--ink-400)',
    fontSize: '13px',
  },
};
